import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function ConfirmarEmail() {
  const navigate = useNavigate();
  const { resendConfirmation, user } = useAuth();
  const [resendTimer, setResendTimer] = useState(60);
  const [resendMessage, setResendMessage] = useState('');
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const email = localStorage.getItem('orcai_pending_email') || '';

  // If already confirmed, redirect
  useEffect(() => {
    if (user?.email_confirmed) {
      navigate('/escolher-plano');
    }
  }, [user, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleResend = async () => {
    if (resendTimer > 0 || resending) return;
    setResending(true);
    setResendMessage('');
    setError('');
    const result = await resendConfirmation(email);
    setResending(false);
    if (result.error) {
      setError(result.error);
    } else {
      setResendMessage('Email reenviado com sucesso!');
      setResendTimer(60);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary via-secondary/95 to-primary/20 flex items-center justify-center px-4">
        <div className="bg-card rounded-2xl p-8 shadow-2xl shadow-black/20 max-w-md w-full text-center">
          <p className="text-muted-foreground mb-4">Nenhum email pendente de confirmação.</p>
          <Link to="/registro" className="text-accent font-semibold hover:text-secondary no-underline">
            Criar conta
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-secondary/95 to-primary/20 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 no-underline justify-center">
            <div className="w-12 h-12 rounded-xl bg-gradient-cta flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-base">QCO</span>
            </div>
            <span className="font-display font-bold text-3xl text-white">QuantoCustaObra</span>
          </Link>
        </div>

        <div className="bg-card rounded-2xl p-8 shadow-2xl shadow-black/20">
          {/* Email icon */}
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-secondary text-center mb-2 font-display">Verifique seu email</h1>
          <p className="text-muted-foreground text-center text-sm mb-6">
            Enviamos um link de confirmação para{' '}
            <strong className="text-foreground">{email}</strong>.
            <br />Clique no link para ativar sua conta.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
              {error}
            </div>
          )}

          {resendMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm text-center">
              {resendMessage}
            </div>
          )}

          {/* Resend button */}
          <div className="text-center mb-4">
            {resendTimer > 0 ? (
              <p className="text-sm text-muted-foreground">
                Reenviar email em <strong>{resendTimer}s</strong>
              </p>
            ) : (
              <button
                onClick={handleResend}
                disabled={resending}
                className="w-full py-3 rounded-xl border-2 border-primary text-primary font-bold text-sm hover:bg-primary/5 transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {resending ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Reenviando...
                  </span>
                ) : 'Reenviar email'}
              </button>
            )}
          </div>

          <div className="text-center">
            <Link to="/login" className="text-sm text-accent font-semibold hover:text-secondary no-underline">
              Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfirmarEmail;
